import { uniq } from 'lodash';
import { defaultStyleFnMapper, dynamicStyleParsers, RICOS_TEXT_COLOR_TYPE, RICOS_TEXT_HIGHLIGHT_TYPE, RICOS_FONT_SIZE_TYPE, RICOS_ITALIC_TYPE, RICOS_BOLD_TYPE, TEXT_COLOR_TYPE, TEXT_HIGHLIGHT_TYPE } from 'wix-rich-content-common';
import { getSelectionRange, isInSelectionRange, getSelectedBlocks } from './draftUtils.js';
import { EditorState, Modifier } from '@wix/draft-js';
import 'immutable';
import '@wix/draft-js/lib/DraftOffsetKey';
import '../consts.js';
import 'wix-rich-content-common/libs/contentAnalytics';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const getBlockStyleRanges = (block, styleSelectionPredicate) => {
  const styleRanges = [];
  block.findStyleRanges(
    (character) => {
      const styles = character.getStyle();
      return styles.toArray().filter((style) => styleSelectionPredicate ? styleSelectionPredicate(style) : style);
    },
    (start, end) => {
      const styles = block.getInlineStyleAt(start).filter((style) => styleSelectionPredicate ? styleSelectionPredicate(style) : style);
      styles.toArray().forEach((style) => styleRanges.push({ start, end, style }));
    }
  );
  return styleRanges;
};
const flatStyleRanges = (blockStyleRanges = []) => {
  const ranges = [blockStyleRanges[0]];
  blockStyleRanges.slice(1).forEach((range) => {
    const index = ranges.length - 1;
    if (ranges[index].end === (range == null ? void 0 : range.start) && ranges[index].style === range.style) {
      ranges[index].end = range.end;
    } else {
      ranges.push(range);
    }
  });
  return ranges;
};
const hasOneStyleInSelection = (block, editorState, styleSelectionPredicate) => {
  var _a, _b;
  const blockSelectionRange = getSelectionRange(editorState, block);
  const blockStyleRanges = flatStyleRanges(
    getBlockStyleRanges(block, styleSelectionPredicate).filter(
      (range) => isInSelectionRange(blockSelectionRange, [range.start, range.end])
    )
  );
  return blockStyleRanges.length === 1 && ((_a = blockStyleRanges[0]) == null ? void 0 : _a.start) <= blockSelectionRange[0] && ((_b = blockStyleRanges[0]) == null ? void 0 : _b.end) >= blockSelectionRange[1];
};
const getSelectionStyles = (editorState, styleSelectionPredicate) => {
  const selectedBlocks = getSelectedBlocks(editorState);
  return uniq(
    selectedBlocks.reduce((selectedStyles, block) => {
      const blockSelectionRange = getSelectionRange(editorState, block);
      const blockStyleRanges = getBlockStyleRanges(block, styleSelectionPredicate);
      return [
        ...selectedStyles,
        ...blockStyleRanges.filter((range) => isInSelectionRange(blockSelectionRange, [range.start, range.end])).map((range) => range.style)
      ];
    }, [])
  );
};
const removeCurrentInlineStyle = (editorState, styleSelectionPredicate) => {
  const selection = editorState.getSelection();
  if (selection.isCollapsed()) {
    const currentStyles2 = editorState.getCurrentInlineStyle();
    const currentStylesAsArray = currentStyles2.toJS();
    let newCurrentStyles = currentStyles2;
    currentStylesAsArray.forEach((style) => {
      if (styleSelectionPredicate == null ? void 0 : styleSelectionPredicate(style)) {
        newCurrentStyles = newCurrentStyles.remove(style);
      }
    });
    return EditorState.setInlineStyleOverride(editorState, newCurrentStyles);
  }
  const currentStyles = getSelectionStyles(editorState, styleSelectionPredicate);
  return currentStyles.reduce((nextEditorState, style) => {
    const contentState = nextEditorState.getCurrentContent();
    const nextContentState = Modifier.removeInlineStyle(contentState, selection, style);
    return EditorState.push(nextEditorState, nextContentState, "change-inline-style");
  }, editorState);
};
const setInlineStyle = (editorState, inlineStyle) => {
  const selection = editorState.getSelection();
  if (selection.isCollapsed()) {
    const currentStyle = editorState.getCurrentInlineStyle();
    return EditorState.setInlineStyleOverride(editorState, currentStyle.add(inlineStyle));
  }
  const contentState = Modifier.applyInlineStyle(
    editorState.getCurrentContent(),
    selection,
    inlineStyle
  );
  return EditorState.push(editorState, contentState, "change-inline-style");
};
const getDefaultStyleFn = (type) => (styles) => styles.toArray().reduce((cssStyle, style) => {
  return __spreadValues(__spreadValues({}, cssStyle), defaultStyleFnMapper[type](style));
}, {});
const getCustomStyleFn = (customStyleFn, styleSelectionPredicate, type) => {
  const styleParser = dynamicStyleParsers[type];
  return (styles) => {
    const filteredStyles = styles.filter((style) => styleParser(style, styleSelectionPredicate)).map((style) => styleParser(style, styleSelectionPredicate));
    return customStyleFn(filteredStyles);
  };
};
const getCustomStyleFns = (config) => {
  const { [TEXT_COLOR_TYPE]: colorSetting = {}, [TEXT_HIGHLIGHT_TYPE]: highlightSettings = {} } = config;
  const colorTypes = [
    { type: RICOS_TEXT_COLOR_TYPE, setting: colorSetting },
    { type: RICOS_TEXT_HIGHLIGHT_TYPE, setting: highlightSettings }
  ];
  const inlineStyleTypes = [
    RICOS_FONT_SIZE_TYPE,
    RICOS_ITALIC_TYPE,
    RICOS_BOLD_TYPE
  ];
  return [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...colorTypes.map((style) => {
      const {
        type,
        setting: { customStyleFn, styleSelectionPredicate }
      } = style;
      return customStyleFn ? getCustomStyleFn(customStyleFn, styleSelectionPredicate, type) : getDefaultStyleFn(type);
    }),
    ...inlineStyleTypes.map((type) => getDefaultStyleFn(type))
  ];
};

export { getBlockStyleRanges, getCustomStyleFn, getCustomStyleFns, getDefaultStyleFn, getSelectionStyles, hasOneStyleInSelection, removeCurrentInlineStyle, setInlineStyle };
//# sourceMappingURL=inlineStyleUtils.js.map
